import * as React from 'react';
import {Select, Tooltip} from '@packages/systems/spring';
import {Alignment as RiveAlignment, Fit as RiveFit} from '@rive-app/webgl2';

const DEFAULT_FIT_OPTION = {
  value: RiveFit.Contain,
  label: 'Contain (default)',
  // note: 'Rive content will be contained within the view, preserving the aspect ratio. If the ratios differ, then a portion of the view will be unused.',
};

const FIT_OPTIONS = [
  DEFAULT_FIT_OPTION,
  {
    value: RiveFit.Cover,
    label: 'Cover',
    // note: 'Rive will cover the view, preserving the aspect ratio. If the Rive content has a different ratio to the view, then the Rive content will be clipped.',
  },
  {
    value: RiveFit.Fill,
    label: 'Fill',
    // note: 'Rive content will fill the available view. If the aspect ratios differ, then the Rive content will be stretched.',
  },
  {
    value: RiveFit.FitWidth,
    label: 'Fit width',
    // note: 'Rive content will fill to the width of the view. This may result in clipping or unfilled view space.',
  },
  {
    value: RiveFit.FitHeight,
    label: 'Fit height',
    // note: 'Rive content will fill to the height of the view. This may result in clipping or unfilled view space.',
  },
  {
    value: RiveFit.None,
    label: 'None',
    // note: 'Rive content will render to the size of its artboard, which may result in clipping or unfilled view space.',
  },
  {
    value: RiveFit.ScaleDown,
    label: 'Scale down',
    // note: 'Rive content is scaled down to the size of the view, preserving the aspect ratio. This is equivalent to Contain when the content is larger than the canvas. If the canvas is larger, then ScaleDown will not scale up.',
  },
];

const DEFAULT_ALIGNMENT_OPTION = {
  value: RiveAlignment.Center,
  label: 'Center (default)',
};

const ALIGNMENT_OPTIONS = [
  DEFAULT_ALIGNMENT_OPTION,
  {value: RiveAlignment.TopCenter, label: 'Top center'},
  {value: RiveAlignment.TopRight, label: 'Top right'},
  {value: RiveAlignment.CenterLeft, label: 'Center left'},
  {value: RiveAlignment.CenterRight, label: 'Center right'},
  {value: RiveAlignment.BottomLeft, label: 'Bottom left'},
  {value: RiveAlignment.BottomCenter, label: 'Bottom center'},
  {value: RiveAlignment.BottomRight, label: 'Bottom right'},
];

export type FitType = `${RiveFit}`;
export type AlignmentType = `${RiveAlignment}`;
type SelectProps = React.ComponentProps<typeof Select>;

export const RiveFitSelect = ({
  value,
  onChange,
  isDisabled,

  'aria-label': ariaLabel,
  'data-automation-id': dataAutomationId,
  'aria-labelledby': ariaLabelledBy,
}: {
  value: FitType | undefined;
  onChange: (value: RiveFit) => void;
  isDisabled?: boolean;

  'data-automation-id'?: SelectProps['data-automation-id'];
  'aria-label'?: SelectProps['aria-label'];
  'aria-labelledby'?: SelectProps['aria-labelledby'];
}) => {
  return (
    <Tooltip
      asChild
      label="Fit determines how the Rive content will be fitted to the view."
    >
      <Select
        aria-label={ariaLabel}
        data-automation-id={dataAutomationId}
        aria-labelledby={ariaLabelledBy}
        placeholder="Select a fit..."
        onChange={onChange}
        isDisabled={isDisabled}
        options={FIT_OPTIONS}
        value={value || DEFAULT_FIT_OPTION.value}
      />
    </Tooltip>
  );
};

export const RiveAlignmentSelect = ({
  value,
  onChange,
  isDisabled,
  'aria-label': ariaLabel,
  'data-automation-id': dataAutomationId,
  'aria-labelledby': ariaLabelledBy,
}: {
  value: AlignmentType | undefined;
  onChange: (value: RiveAlignment) => void;
  isDisabled?: boolean;

  'data-automation-id'?: SelectProps['data-automation-id'];
  'aria-label'?: SelectProps['aria-label'];
  'aria-labelledby'?: SelectProps['aria-labelledby'];
}) => {
  return (
    <Tooltip
      asChild
      label="Alignment determines how the content aligns with respect to the view bounds."
    >
      <Select
        aria-label={ariaLabel}
        data-automation-id={dataAutomationId}
        aria-labelledby={ariaLabelledBy}
        placeholder="Select an alignment..."
        onChange={onChange}
        isDisabled={isDisabled}
        options={ALIGNMENT_OPTIONS}
        value={value || DEFAULT_ALIGNMENT_OPTION.value}
      />
    </Tooltip>
  );
};
